import { useMemo } from 'react';
import { Layout } from '../../components/Layout';
import Content from './Content';
import { useSelector } from 'react-redux';
import {
  selectSubscriptionSummaries,
  selectIsLoadingSubscriptionSummaries,
  selectActiveCluster,
  setActiveClusterId,
  setActiveSubscriptionId,
} from '../../store/slices/subscription';
import { Transaction } from 'grts-utils/src/types/Transaction';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useGetSubscriptionSummariesByUserIdQuery } from '../../store/services/api';
import { selectUserId } from '../../store/slices/user';
import useWidthBreakpoint from '../../hooks/useWidthBreakpoint';

const Overview: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = useSelector(selectUserId);

  useGetSubscriptionSummariesByUserIdQuery(userId, {
    refetchOnMountOrArgChange: true,
  });

  const data = useSelector(selectSubscriptionSummaries);
  const isLoading = useSelector(selectIsLoadingSubscriptionSummaries);
  const activeCluster = useSelector(selectActiveCluster);
  const { isMatch } = useWidthBreakpoint();

  const activeClusterSubscriptions = useMemo(() => {
    return data
      .filter((s) =>
        activeCluster === null ? true : s.clusterId === activeCluster,
      )
      .sort((a, b) => b.totalBalanceInStable - a.totalBalanceInStable);
  }, [activeCluster, data]);

  const totalValueByCluster = useMemo(() => {
    if (!data.length) {
      return {};
    }

    return data.reduce<Record<string, number>>((acc, sub) => {
      acc[sub.clusterId] = (acc[sub.clusterId] || 0) + sub.totalBalanceInStable;
      return acc;
    }, {});
  }, [data]);

  const costBasisByCluster = useMemo(() => {
    if (!data.length) {
      return {};
    }

    return data.reduce<Record<string, number>>((acc, sub) => {
      acc[sub.clusterId] =
        (acc[sub.clusterId] || 0) + (sub.profits?.costBasis.stable || 0);
      return acc;
    }, {});
  }, [data]);

  const totalStableProfitByCluster = useMemo(() => {
    const clusterIds = Object.keys(totalValueByCluster);
    return clusterIds.reduce<Record<string, number>>((acc, curr) => {
      acc[curr] =
        (totalValueByCluster[curr] - costBasisByCluster[curr]) /
        costBasisByCluster[curr];
      return acc;
    }, {});
  }, [costBasisByCluster, totalValueByCluster]);

  const entirePortfolioValue = useMemo(() => {
    return Object.values(totalValueByCluster).reduce(
      (acc, curr) => acc + curr,
      0,
    );
  }, [totalValueByCluster]);

  const entirePortfolioProfit = useMemo(() => {
    const totalCostBasis = Object.values(costBasisByCluster).reduce(
      (acc, curr) => acc + curr,
      0,
    );

    if (totalCostBasis === 0) {
      return 0;
    }

    return (entirePortfolioValue - totalCostBasis) / totalCostBasis;
  }, [costBasisByCluster, entirePortfolioValue]);

  const activeClusterTotalValue = useMemo(() => {
    return activeCluster === null
      ? entirePortfolioValue
      : totalValueByCluster[activeCluster];
  }, [activeCluster, entirePortfolioValue, totalValueByCluster]);

  const hasPositiveProfit = useMemo(() => {
    if (!data) {
      return undefined;
    }

    if (!activeCluster) {
      if (entirePortfolioProfit === 0) {
        return undefined;
      }

      return entirePortfolioProfit > 0;
    }

    return totalStableProfitByCluster[activeCluster] >= 0;
  }, [activeCluster, data, entirePortfolioProfit, totalStableProfitByCluster]);

  const transactions = useMemo(
    () =>
      activeClusterSubscriptions
        .map((s) => s.latestTransaction)
        .filter((t) => !!t)
        .sort(
          (a, b) =>
            (b?.transactionTimestamp || 0) - (a?.transactionTimestamp || 0),
        ),
    [activeClusterSubscriptions],
  );

  const handleSubscriptionClick = (subscriptionId: number) => {
    dispatch(setActiveSubscriptionId(subscriptionId));
    navigate('sub/');
  };

  const hasData = !!Object.keys(totalValueByCluster).length;
  const hasMultipleClusters = Object.keys(totalValueByCluster).length > 1;

  const activeClusterItem = useMemo(() => {
    if (activeCluster === null) {
      return (
        <Layout.SideMenuItem
          id={'Portfolio'}
          label={'Portfolio'}
          value={entirePortfolioValue}
          delta={entirePortfolioProfit}
        />
      );
    }

    return (
      <Layout.SideMenuItem
        key={activeCluster}
        id={activeCluster}
        label={activeCluster}
        value={totalValueByCluster[activeCluster]}
        delta={totalStableProfitByCluster[activeCluster]}
      />
    );
  }, [
    activeCluster,
    entirePortfolioProfit,
    entirePortfolioValue,
    totalStableProfitByCluster,
    totalValueByCluster,
  ]);

  const clusterOptionsItems = useMemo(() => {
    if (activeCluster === null) {
      return Object.entries(totalValueByCluster).map(([clusterId, value]) => (
        <Layout.SideMenuItem
          key={clusterId}
          id={clusterId}
          label={clusterId}
          value={value}
          delta={totalStableProfitByCluster[clusterId]}
          onClick={(clusterId: string) =>
            dispatch(setActiveClusterId(clusterId))
          }
          isActive={activeCluster === clusterId}
        />
      ));
    }

    return [
      <Layout.SideMenuItem
        key={'Portfolio'}
        id={'Portfolio'}
        label={'Portfolio'}
        value={entirePortfolioValue}
        delta={entirePortfolioProfit}
        onClick={() => dispatch(setActiveClusterId(null))}
      />,
      ...Object.entries(totalValueByCluster)
        .filter(([clusterId]) => clusterId !== activeCluster)
        .map(([clusterId, value]) => (
          <Layout.SideMenuItem
            key={clusterId}
            id={clusterId}
            label={clusterId}
            value={value}
            delta={totalStableProfitByCluster[clusterId]}
            onClick={(clusterId: string) =>
              dispatch(setActiveClusterId(clusterId))
            }
            isActive={activeCluster === clusterId}
          />
        )),
    ];
  }, [
    activeCluster,
    dispatch,
    entirePortfolioProfit,
    entirePortfolioValue,
    totalStableProfitByCluster,
    totalValueByCluster,
  ]);

  return (
    <Layout hasPositiveProfit={hasPositiveProfit}>
      {hasData ? (
        <Layout.SideMenu hasPositiveProfit={hasPositiveProfit}>
          <>
            <Layout.SideMenuSection>
              {isMatch('md') ? (
                <>
                  {hasMultipleClusters ? (
                    <>
                      <Layout.SideMenuTitle title="Clusters" />
                      <Layout.SideMenuItem
                        id={'Portfolio'}
                        label={'Portfolio'}
                        value={entirePortfolioValue}
                        delta={entirePortfolioProfit}
                        onClick={() => dispatch(setActiveClusterId(null))}
                        isActive={activeCluster === null}
                      />
                    </>
                  ) : (
                    <Layout.SideMenuTitle title="Portfolio" />
                  )}

                  <>
                    {Object.entries(totalValueByCluster).map(
                      ([clusterId, value]) => (
                        <Layout.SideMenuItem
                          key={clusterId}
                          id={clusterId}
                          label={clusterId}
                          value={value}
                          delta={totalStableProfitByCluster[clusterId]}
                          onClick={(clusterId: string) =>
                            dispatch(setActiveClusterId(clusterId))
                          }
                          isActive={activeCluster === clusterId}
                        />
                      ),
                    )}
                  </>
                </>
              ) : (
                <>
                  <Layout.SideMenuTitle title="Active Cluster" />
                  <Layout.SideMenuSelect
                    activeItem={activeClusterItem}
                    options={clusterOptionsItems}
                  />
                </>
              )}
            </Layout.SideMenuSection>

            {isMatch('md') && activeClusterSubscriptions?.length ? (
              <Layout.SideMenuSection>
                <Layout.SideMenuTitle title="Subscriptions" />
                <>
                  {activeClusterSubscriptions.map((sub) => (
                    <Layout.SideMenuItem
                      key={sub.id}
                      id={sub.id}
                      label={sub.botFriendlyName}
                      value={sub.totalBalanceInStable}
                      delta={sub.currentProfit}
                      onClick={handleSubscriptionClick}
                    />
                  ))}
                </>
              </Layout.SideMenuSection>
            ) : undefined}
          </>
        </Layout.SideMenu>
      ) : undefined}

      <Layout.Content>
        <Content
          // key={activeCluster}
          totalValue={activeClusterTotalValue}
          subscriptions={activeClusterSubscriptions}
          isLoading={isLoading}
          transactions={transactions as Transaction[]}
        />
      </Layout.Content>
    </Layout>
  );
};

export default Overview;
